import html2canvas from 'html2canvas';

// Snapshots a container's live DOM into an HTML string for PDF export. Most of
// the Benchmark content is inline SVG + DOM that clones faithfully, but a few
// elements are too wide for a portrait page (the HeatmapV2 grid scrolls
// horizontally). Those are rasterized to a PNG and dropped in as an <img> that
// scales to the page width — so every column stays visible however wide it is.
//
// html2canvas is reused here deliberately: it is already a dependency and the
// heatmap's own "Download as Jpg" button rasterizes this same element with it.

// Rasterize one element to a PNG data URL. The heatmap scrolls inside
// `.ant-table-content`, so the element is temporarily widened to its full scroll
// width (nothing clipped), then its inline styles are restored.
const rasterize = async (node) => {
    const scroller = node.querySelector('.ant-table-content') || node;
    const fullWidth = scroller.scrollWidth + 50;
    const prev = {
        width: node.style.width,
        maxWidth: node.style.maxWidth,
        padding: node.style.padding,
    };
    node.style.width = `${fullWidth}px`;
    node.style.maxWidth = `${fullWidth}px`;
    node.style.padding = '10px';
    try {
        const canvas = await html2canvas(node, {
            scale: 2, // crisp text when the image is scaled down to fit the page
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
        });
        return canvas.toDataURL('image/png');
    } finally {
        node.style.width = prev.width;
        node.style.maxWidth = prev.maxWidth;
        node.style.padding = prev.padding;
    }
};

// Returns { html, width }. `rasterizeIds` are element ids replaced by a fitted
// image; `fitSvgIds` are wrappers whose chart <svg> is made to scale to its
// container (viewBox + width:100%) instead of keeping a fixed pixel size that
// overflows the narrower print page; `stripSelectors` are removed from the
// export (e.g. download buttons); `keepScrollIds` are horizontal scrollers that
// print exactly the slice currently scrolled into view (a cloned node loses its
// scrollLeft, so the content is offset by it and the overflow clipped).
export const capturePdfBody = async (container, options = {}) => {
    const { rasterizeIds = [], fitSvgIds = [], keepScrollIds = [], stripSelectors = [] } = options;
    const width = Math.round(container.getBoundingClientRect().width);

    const scrollOffsets = {};
    keepScrollIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) scrollOffsets[id] = el.scrollLeft;
    });

    // Measure each live chart SVG's drawn size; it becomes the clone's viewBox.
    const svgSizes = {};
    fitSvgIds.forEach((id) => {
        const svg = document.querySelector(`[id="${id}"] svg`);
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        if (rect.width && rect.height) svgSizes[id] = { w: Math.round(rect.width), h: Math.round(rect.height) };
    });

    // Rasterize the LIVE nodes first — html2canvas needs real layout.
    const images = {};
    for (const id of rasterizeIds) {
        const live = document.getElementById(id);
        if (!live) continue;
        try {
            images[id] = await rasterize(live);
        } catch (e) {
            images[id] = null; // fall back to the cloned DOM for this one
        }
    }

    // Clone, swap rasterized nodes for <img>, then drop unwanted controls.
    const clone = container.cloneNode(true);
    rasterizeIds.forEach((id) => {
        const target = clone.querySelector(`[id="${id}"]`);
        if (!target || !images[id]) return;
        const img = document.createElement('img');
        img.src = images[id];
        img.alt = '';
        img.style.width = '100%';
        img.style.height = 'auto';
        img.style.display = 'block';
        target.replaceWith(img);
    });
    Object.keys(svgSizes).forEach((id) => {
        const target = clone.querySelector(`[id="${id}"]`);
        const svg = target && target.querySelector('svg');
        if (!svg) return;
        const { w, h } = svgSizes[id];
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
        svg.removeAttribute('width');
        svg.removeAttribute('height');
        svg.style.width = '100%';
        svg.style.height = 'auto';
        // The chart lib wraps the svg in divs with fixed pixel sizes; free them
        // so the svg can shrink to the card.
        for (let el = svg.parentElement; el && el !== target.parentElement; el = el.parentElement) {
            el.style.width = '100%';
            el.style.height = 'auto';
        }
    });
    Object.keys(scrollOffsets).forEach((id) => {
        const target = clone.querySelector(`[id="${id}"]`);
        if (!target || !target.firstElementChild) return;
        target.style.overflow = 'hidden';
        target.firstElementChild.style.marginLeft = `-${scrollOffsets[id]}px`;
    });
    stripSelectors.forEach((sel) => {
        clone.querySelectorAll(sel).forEach((node) => node.remove());
    });

    return { html: clone.innerHTML, width };
};
